import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useGameSocket } from "../lib/useGameSocket";
import { CurrentBall, CalledBoard } from "../components/BingoBits";
import { STAGE_LABELS } from "../lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Trophy, Volume2, VolumeX } from "lucide-react";
import { fireConfetti, playFanfare, playAlert } from "../lib/celebrate";
import { ReactionsOverlay, useReactionQueue, useAnnouncer } from "../components/StudioFx";
import { Scoreboard } from "../components/Scoreboard";
import { BuyQr } from "../components/BuyQr";
import { TensionMeter } from "../components/TensionMeter";

export default function Studio() {
  const [gameId, setGameId] = useState(null);
  useEffect(() => {
    api.get("/games/latest").then((r) => setGameId(r.data?.id || null));
  }, []);

  const [pendingClaim, setPendingClaim] = useState(null);
  const [winner, setWinner] = useState(null);
  const [voice, setVoice] = useState(localStorage.getItem("studio_voice") !== "off");
  const reactions = useReactionQueue();
  const { game, viewers } = useGameSocket(gameId, (msg) => {
    if (msg.type === "reaction") reactions.push(msg.kind);
    if (msg.type === "bingo_claim" || msg.type === "bingo_claim_polled") playAlert();
    if (msg.type === "claim_verified" && msg.approve) {
      setWinner({ name: msg.display_name, stage: msg.stage });
      fireConfetti(6000);
      playFanfare();
      setTimeout(() => setWinner(null), 9000);
    }
  });

  useEffect(() => {
    if (!gameId) return;
    if (game?.pending_claim_id) {
      api.get(`/claims/${game.pending_claim_id}`).then((r) => setPendingClaim(r.data)).catch(() => {});
    } else {
      setPendingClaim(null);
    }
  }, [gameId, game?.pending_claim_id]);

  const last = game?.drawn?.length ? game.drawn[game.drawn.length - 1] : null;
  useAnnouncer(last, voice);
  const toggleVoice = () => {
    const next = !voice;
    setVoice(next);
    localStorage.setItem("studio_voice", next ? "on" : "off");
    if (next) { try { window.speechSynthesis.speak(new SpeechSynthesisUtterance("")); } catch (e) { /* ignore */ } }
  };

  const soundCheck = () => {
    playFanfare();
    fireConfetti(1500);
    try {
      const u = new SpeechSynthesisUtterance("Geluidstest. Nummer 42.");
      u.lang = "nl-NL";
      window.speechSynthesis.speak(u);
    } catch (e) { /* ignore */ }
  };

  if (!gameId) {
    return (
      <div className="min-h-screen bg-[#0B0C10] text-slate-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-black text-[#FFD700]" style={{ fontFamily: "Unbounded, sans-serif" }}>Geen actief spel</h1>
          <p className="mt-3 text-slate-400">Wacht op de admin om een nieuw spel aan te maken.</p>
        </div>
      </div>
    );
  }
  if (!game) return null;

  return (
    <div className="min-h-screen bg-[#0B0C10] text-slate-100 overflow-hidden">
      <ReactionsOverlay items={reactions.items} />
      <div className="fixed top-4 right-4 z-40 flex gap-2">
        <button data-testid="studio-sound-check" onClick={soundCheck} title="Geluidstest"
                className="px-3 py-2 rounded-full border border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700] text-xs font-bold uppercase tracking-widest hover:bg-[#FFD700]/20 transition">
          Geluidstest
        </button>
        <button data-testid="studio-voice-toggle" onClick={toggleVoice} title={voice ? "Omroeper aan" : "Omroeper uit"}
                className={`p-3 rounded-full border transition ${voice ? "bg-[#00E676]/20 border-[#00E676]/50 text-[#00E676]" : "bg-white/5 border-white/10 text-slate-500"}`}>
          {voice ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
        </button>
      </div>

      {game.status === "finished" && <Scoreboard game={game} />}
      <div className="pointer-events-none absolute inset-0" style={{
        backgroundImage: "radial-gradient(circle at 15% 10%, rgba(255,215,0,0.15), transparent 45%), radial-gradient(circle at 85% 90%, rgba(255,0,127,0.15), transparent 45%)",
      }} />
      <div className="relative p-8 lg:p-12 grid grid-cols-12 gap-8 min-h-screen">
        {/* Left: current ball */}
        <div className="col-span-12 lg:col-span-5 flex flex-col justify-between">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF1744]/20 border border-[#FF1744]/50 text-[#FF1744] text-xs uppercase tracking-[0.3em] mb-6">
              <span className="w-2 h-2 rounded-full bg-[#FF1744] animate-pulse" /> Live
            </div>
            <h1 className="text-5xl xl:text-6xl font-black leading-tight" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="studio-title">
              {game.title}
            </h1>
            <p className="text-slate-400 uppercase tracking-[0.3em] text-sm mt-3">{STAGE_LABELS[game.stage]}</p>
          </div>

          <div className="mt-8 flex justify-center">
            <AnimatePresence mode="wait">
              <motion.div key={last ?? "empty"}
                initial={{ scale: 0.3, opacity: 0, rotate: -180 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                exit={{ scale: 0.3, opacity: 0 }}
                transition={{ type: "spring", stiffness: 220, damping: 18 }}>
                <CurrentBall number={last} />
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-8 flex flex-wrap gap-4">
            <div className="flex-1 min-w-[140px] p-4 rounded-xl bg-[#12141D] border border-white/10">
              <div className="text-xs uppercase tracking-widest text-slate-400">Getrokken</div>
              <div className="text-4xl font-black text-[#FFD700] mt-1" data-testid="studio-count">{game.drawn.length}<span className="text-lg text-slate-500">/90</span></div>
            </div>
            <div className="flex-1 min-w-[140px] p-4 rounded-xl bg-[#12141D] border border-white/10">
              <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-1"><Users className="w-3 h-3" /> Kijkers</div>
              <div className="text-4xl font-black text-[#00F0FF] mt-1" data-testid="studio-viewers">{viewers}</div>
            </div>
            <div className="flex-1 min-w-[140px] p-4 rounded-xl bg-[#12141D] border border-white/10">
              <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-1"><Trophy className="w-3 h-3" /> Prijs</div>
              <div className="text-3xl font-black text-[#FF007F] mt-1">€{game.prizes[game.stage]}</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TensionMeter gameId={gameId} drawnCount={game.drawn.length} stage={game.stage} />
            <BuyQr price={game.ticket_price} />
          </div>
        </div>

        {/* Right: board */}
        <div className="col-span-12 lg:col-span-7 flex flex-col">
          <div className="text-xs uppercase tracking-[0.3em] text-slate-400 mb-3">Alle nummers 1 — 90</div>
          <CalledBoard drawn={game.drawn} currentBig />
          <div className="mt-6 flex gap-2 overflow-x-auto">
            {game.drawn.slice(-8).map((n, i) => (
              <div key={i} className="min-w-[64px] h-16 rounded-lg bg-[#12141D] border border-[#FFD700]/30 flex items-center justify-center text-2xl font-black text-[#FFD700]">
                {n}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Winner celebration */}
      <AnimatePresence>
        {winner && !pendingClaim && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="studio-winner-overlay"
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-8">
            <motion.div initial={{ scale: 0.4, y: 60 }} animate={{ scale: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 180, damping: 14 }}
              className="text-center">
              <div className="text-[#00E676] text-2xl uppercase tracking-[0.5em] mb-4">Winnaar</div>
              <h2 className="text-7xl lg:text-9xl font-black text-[#FFD700] drop-shadow-[0_0_40px_rgba(255,215,0,0.8)]" style={{ fontFamily: "Unbounded, sans-serif" }}
                  data-testid="studio-winner-name">
                {winner.name}
              </h2>
              <div className="mt-6 text-3xl text-slate-200">{STAGE_LABELS[winner.stage]}</div>
              <div className="mt-3 text-5xl font-black text-[#FF007F]">€{game.prizes[winner.stage]}</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bingo overlay */}
      <AnimatePresence>
        {pendingClaim && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-8">
            <motion.div initial={{ scale: 0.5, rotate: -5 }} animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="text-center max-w-3xl">
              <div className="text-[#FF007F] text-2xl uppercase tracking-[0.5em] mb-4 animate-pulse">Bingo Claim!</div>
              <h2 className="text-8xl lg:text-9xl font-black bg-gradient-to-r from-[#FFD700] via-[#FF007F] to-[#00F0FF] bg-clip-text text-transparent" style={{ fontFamily: "Unbounded, sans-serif" }}>
                BINGO!
              </h2>
              <div className="mt-6 text-3xl font-bold" data-testid="studio-claim-name">{pendingClaim.display_name}</div>
              <div className="mt-2 text-slate-300">Claimt: {STAGE_LABELS[pendingClaim.stage]}</div>
              <div className="mt-6 text-slate-400 text-sm uppercase tracking-widest">Presentator controleert de kaart...</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
